<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { searchActorsTypeahead } from '../services/bluesky';

const props = defineProps({
  query: {
    type: String,
    default: ''
  },
  position: {
    type: Object,
    default: () => ({ top: 0, left: 0 })
  }
});

const emit = defineEmits(['select', 'close']);

const users = ref([]);
const loading = ref(false);
const selectedIndex = ref(0);
const container = ref(null);

const fetchUsers = async (q) => {
  if (!q) {
    users.value = [];
    return;
  }
  loading.value = true;
  try {
    const results = await searchActorsTypeahead(q, 8);
    users.value = results;
    selectedIndex.value = 0;
  } catch (e) {
    console.error('Mention search failed', e);
  } finally {
    loading.value = false;
  }
};

watch(() => props.query, (newQuery) => {
  fetchUsers(newQuery);
});

onMounted(() => {
  fetchUsers(props.query);
  window.addEventListener('keydown', handleKeyDown);
  // Close when clicking outside
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('mousedown', handleClickOutside);
});

const handleKeyDown = (e) => {
  if (users.value.length === 0) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = (selectedIndex.value + 1) % users.value.length;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value = (selectedIndex.value - 1 + users.value.length) % users.value.length;
  } else if (e.key === 'Enter') {
    e.preventDefault();
    selectUser(users.value[selectedIndex.value]);
  } else if (e.key === 'Escape') {
    emit('close');
  }
};

const handleClickOutside = (e) => {
  if (container.value && !container.value.contains(e.target)) {
    emit('close');
  }
};

const selectUser = (user) => {
  emit('select', user);
};
</script>

<template>
  <Teleport to="body">
    <div 
      ref="container"
      class="mention-suggestions elevation-4"
      :style="{ top: position.top + 'px', left: position.left + 'px' }"
    >
      <div v-if="loading && users.length === 0" class="pa-4 text-center">
        <v-progress-circular indeterminate size="20" width="2" color="primary"></v-progress-circular>
      </div>
      
      <div v-else-if="users.length === 0 && !loading" class="pa-4 text-center text-caption text-secondary">
        No users found
      </div>
  
      <v-list v-else density="compact" class="pa-0 bg-transparent">
        <v-list-item
          v-for="(user, index) in users"
          :key="user.did"
          :active="index === selectedIndex"
          @click="selectUser(user)"
          @mouseenter="selectedIndex = index"
          class="user-item"
          :style="{ backgroundColor: index === selectedIndex ? 'rgba(255, 255, 255, 0.1) !important' : 'transparent' }"
        >
          <template v-slot:prepend>
            <v-avatar size="32" class="mr-2 border border-white-05">
              <v-img :src="user.avatar || 'https://cdn.bsky.app/img/avatar/plain/did:plc:placeholder/avatar.png'"></v-img>
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold text-white">
            {{ user.displayName || user.handle }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption text-grey-lighten-1">
            @{{ user.handle }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </div>
  </Teleport>
</template>

<style scoped>
.mention-suggestions {
  position: fixed;
  z-index: 99999;
  background: rgba(18, 18, 18, 0.7) !important;
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border-radius: 12px;
  min-width: 280px;
  max-width: 350px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5) !important;
}

.user-item {
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover, .user-item.v-list-item--active {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

/* Custom scrollbar */
.mention-suggestions::-webkit-scrollbar {
  width: 6px;
}
.mention-suggestions::-webkit-scrollbar-track {
  background: transparent;
}
.mention-suggestions::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
.mention-suggestions::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
